import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Building2,
  ChevronLeft,
  ChevronRight,
  Home as HomeIcon,
  Pencil,
  Scale,
  Undo2,
} from 'lucide-react';

import { ApiError, api } from '@/lib/api';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { cn, formatDuration, formatTime } from '@/lib/utils';
import { CorrectionRequestModal } from './corrections/CorrectionRequestModal';
import { VoidedPunchesSection, type VoidedPunch } from '@/components/VoidedPunchesSection';
import type { OvertimeBalance } from './overtime/types';

type WorkdayIncident = 'NO_OUT' | 'OUT_WITHOUT_IN' | 'DOUBLE_IN';
type WorkdayStatus = 'OPEN' | 'CLOSED' | 'INCIDENT';
type DeclaredLocation = 'OFFICE' | 'REMOTE';
type PunchType = 'IN' | 'OUT';

type OvertimeApprovalStatus = 'PENDING' | 'APPROVED' | 'REJECTED';

interface RangeWorkday {
  /** null en días sintéticos (sin fichar / sin fila): no tienen detalle. */
  id: string | null;
  date: string;
  expectedMinutes: number;
  workedMinutes: number;
  breakMinutes: number;
  /** Balance NETO: ya incluye los minutos compensados con la bolsa. */
  balanceMinutes: number;
  status: WorkdayStatus;
  overtimeApprovalStatus: OvertimeApprovalStatus | null;
  /** Minutos del déficit de este día saldados con la bolsa (0 si ninguno). */
  deficitCompensatedMinutes: number;
  /** Id de la compensación activa (para deshacer); null si no hay. */
  compensationId: string | null;
}

interface RangeCause {
  kind: 'ABSENCE' | 'WORKTIME' | 'HOLIDAY';
  label: string;
  color: string | null;
}

interface RangeItem {
  workday: RangeWorkday;
  incidents: WorkdayIncident[];
  cause: RangeCause | null;
}

interface RangeResponse {
  from: string;
  to: string;
  items: RangeItem[];
  summary: {
    totalExpectedMinutes: number;
    totalWorkedMinutes: number;
    totalBreakMinutes: number;
    totalBalanceMinutes: number;
    daysOpen: number;
    daysClosed: number;
    daysIncident: number;
  };
}

interface DetailPunch {
  id: string;
  type: PunchType;
  eventAt: string;
  declaredLocation: DeclaredLocation;
  source: string;
}

interface DetailResponse {
  workday: RangeWorkday;
  incidents: WorkdayIncident[];
  punches: DetailPunch[];
  voidedPunches: VoidedPunch[];
}

const INCIDENT_LABEL: Record<WorkdayIncident, string> = {
  NO_OUT: 'Sin salida',
  OUT_WITHOUT_IN: 'Salida huérfana',
  DOUBLE_IN: 'Entrada doble',
};

const MONTH_NAMES = [
  'enero',
  'febrero',
  'marzo',
  'abril',
  'mayo',
  'junio',
  'julio',
  'agosto',
  'septiembre',
  'octubre',
  'noviembre',
  'diciembre',
];

export function MyDay() {
  const [cursor, setCursor] = useState(() => {
    const n = new Date();
    return { year: n.getFullYear(), month: n.getMonth() };
  });
  // La fila desplegada se identifica por FECHA (YYYY-MM-DD), no por workdayId:
  // los días en los que no se fichó nada no tienen fila Workday (id === null) y
  // aun así deben poder abrirse para solicitar la corrección de ese día.
  const [openDayKey, setOpenDayKey] = useState<string | null>(null);

  const today = useMemo(() => new Date(), []);
  const isCurrentMonth = today.getFullYear() === cursor.year && today.getMonth() === cursor.month;

  const fromYmd = ymd(new Date(cursor.year, cursor.month, 1));
  const toYmd = ymd(new Date(cursor.year, cursor.month + 1, 0));

  const range = useQuery({
    queryKey: ['workdays', 'range', fromYmd, toYmd],
    queryFn: () => api<RangeResponse>(`/workdays/range?from=${fromYmd}&to=${toYmd}`),
  });

  const itemByDate = useMemo(() => {
    const map = new Map<string, RangeItem>();
    for (const it of range.data?.items ?? []) {
      map.set(it.workday.date.slice(0, 10), it);
    }
    return map;
  }, [range.data]);

  const days = useMemo(() => {
    const result: { date: Date; key: string; isToday: boolean; isFuture: boolean }[] = [];
    const last = new Date(cursor.year, cursor.month + 1, 0).getDate();
    for (let d = 1; d <= last; d++) {
      const date = new Date(cursor.year, cursor.month, d);
      const key = ymd(date);
      const isToday = sameYmd(date, today);
      const isFuture = date.getTime() > stripTime(today).getTime();
      result.push({ date, key, isToday, isFuture });
    }
    return result;
  }, [cursor, today]);

  function shiftMonth(delta: -1 | 1) {
    setOpenDayKey(null);
    setCursor((c) => {
      const n = new Date(c.year, c.month + delta, 1);
      return { year: n.getFullYear(), month: n.getMonth() };
    });
  }

  return (
    <div className="p-6 md:p-8">
      <div className="bg-card border border-border rounded-sm shadow-md overflow-hidden">
        <header className="flex items-center justify-between gap-4 px-7 py-5 border-b border-border flex-wrap">
          <div>
            <h2 className="text-lg font-semibold tracking-tightish">Mi jornada</h2>
            <p className="text-[13px] text-muted-foreground mt-1">
              Calendario mensual de fichajes. Click sobre una fila para ver el detalle.
            </p>
          </div>
          <div className="inline-flex items-center gap-2">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => shiftMonth(-1)}
              aria-label="Mes anterior"
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <span
              className="font-mono text-[13px] font-medium uppercase text-muted-foreground px-3 min-w-[160px] text-center"
              style={{ letterSpacing: '0.1em' }}
            >
              {MONTH_NAMES[cursor.month]} {cursor.year}
            </span>
            <Button
              variant="ghost"
              size="icon"
              onClick={() => shiftMonth(1)}
              disabled={isCurrentMonth}
              aria-label="Mes siguiente"
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </header>

        <div className="p-7">
          {range.data ? <MonthSummary summary={range.data.summary} /> : null}

          {range.isLoading ? (
            <p className="text-sm text-muted-foreground mt-6">Cargando…</p>
          ) : range.error ? (
            <p role="alert" className="text-sm text-destructive mt-6">
              {range.error instanceof ApiError ? range.error.message : 'Error cargando jornadas'}
            </p>
          ) : (
            <div className="mt-6 -mx-7 overflow-x-auto">
              <table className="w-full text-[14px]">
                <thead>
                  <tr>
                    <Th>Día</Th>
                    <Th>Estado</Th>
                    <Th align="center">Esperado</Th>
                    <Th align="center">Trabajado</Th>
                    <Th align="center">A compensar</Th>
                    <Th align="center">Balance</Th>
                    <Th align="center">Incidencias</Th>
                  </tr>
                </thead>
                <tbody>
                  {days.map((d) => {
                    const item = itemByDate.get(d.key);
                    const isOpen = openDayKey === d.key;
                    return (
                      <DayRow
                        key={d.key}
                        date={d.date}
                        dayKey={d.key}
                        item={item}
                        isToday={d.isToday}
                        isFuture={d.isFuture}
                        isOpen={isOpen}
                        onToggle={() => setOpenDayKey(isOpen ? null : d.key)}
                      />
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function Th({
  children,
  align = 'left',
}: {
  children: React.ReactNode;
  align?: 'left' | 'center' | 'right';
}) {
  return (
    <th
      className={cn(
        'font-mono text-[12px] font-medium uppercase text-muted-foreground bg-paper border-b border-border px-5 py-3.5',
        align === 'right' && 'text-right',
        align === 'center' && 'text-center',
        align === 'left' && 'text-left',
      )}
      style={{ letterSpacing: '0.08em' }}
    >
      {children}
    </th>
  );
}

function MonthSummary({ summary }: { summary: RangeResponse['summary'] }) {
  const positive = summary.totalBalanceMinutes >= 0;
  return (
    <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
      <Stat label="Esperado" value={formatDuration(summary.totalExpectedMinutes)} />
      <Stat label="Trabajado" value={formatDuration(summary.totalWorkedMinutes)} />
      <Stat
        label="Balance"
        value={`${positive ? '+' : '−'}${formatDuration(Math.abs(summary.totalBalanceMinutes))}`}
        tone={positive ? 'success' : 'destructive'}
      />
      <Stat
        label="Días con incidencia"
        value={String(summary.daysIncident)}
        tone={summary.daysIncident > 0 ? 'warning' : undefined}
      />
    </div>
  );
}

function Stat({
  label,
  value,
  tone,
}: {
  label: string;
  value: string;
  tone?: 'success' | 'destructive' | 'warning';
}) {
  const toneClass =
    tone === 'success'
      ? 'text-success'
      : tone === 'destructive'
        ? 'text-destructive'
        : tone === 'warning'
          ? 'text-warning'
          : 'text-foreground';
  return (
    <div className="border border-border rounded-sm bg-paper px-5 py-4.5">
      <p
        className="font-mono text-[11px] font-medium uppercase text-muted-foreground"
        style={{ letterSpacing: '0.12em' }}
      >
        {label}
      </p>
      <p
        className={cn(
          'font-mono text-[28px] font-medium tracking-tight2 mt-1.5 tabular-nums',
          toneClass,
        )}
      >
        {value}
      </p>
    </div>
  );
}

function DayRow({
  date,
  dayKey,
  item,
  isToday,
  isFuture,
  isOpen,
  onToggle,
}: {
  date: Date;
  dayKey: string;
  item: RangeItem | undefined;
  isToday: boolean;
  isFuture: boolean;
  isOpen: boolean;
  onToggle: () => void;
}) {
  // Todo día no futuro se puede desplegar, tenga o no fichajes: los días sin
  // fichar (workday.id === null) son justamente los que hay que poder corregir.
  const clickable = !isFuture;
  const weekday = date.toLocaleDateString('es-ES', { weekday: 'short' }).slice(0, 3).toUpperCase();
  return (
    <>
      <tr
        className={cn(
          'border-b border-border',
          isFuture && 'opacity-50',
          clickable && 'cursor-pointer hover:bg-paper',
          isToday && 'bg-icaria-100 hover:bg-icaria-100',
        )}
        onClick={clickable ? onToggle : undefined}
      >
        <td className="px-5 py-4">
          <span className="font-mono inline-flex items-baseline gap-2.5 tabular-nums">
            <b className="text-foreground font-semibold text-[15px]">
              {String(date.getDate()).padStart(2, '0')}
            </b>
            <span className="text-muted-foreground text-[12px]">{weekday}</span>
            {isToday ? (
              <span className="text-icaria-600 font-medium text-[12px] ml-0.5">hoy</span>
            ) : null}
          </span>
        </td>
        <td className="px-5 py-4">
          {item ? (
            item.cause ? (
              <CausePill cause={item.cause} />
            ) : (
              <StatusPill status={item.workday.status} />
            )
          ) : (
            <span className="text-sm text-muted-foreground">—</span>
          )}
        </td>
        <td className="px-5 py-4 text-center font-mono tabular-nums">
          {item ? formatDuration(item.workday.expectedMinutes) : '—'}
        </td>
        <td className="px-5 py-4 text-center font-mono tabular-nums">
          {item ? formatDuration(item.workday.workedMinutes) : '—'}
        </td>
        <td className="px-5 py-4 text-center font-mono tabular-nums">
          {item ? (
            <ExtraCell
              minutes={item.workday.balanceMinutes}
              status={item.workday.overtimeApprovalStatus}
            />
          ) : (
            '—'
          )}
        </td>
        <td className="px-5 py-4 text-center font-mono tabular-nums">
          {item ? (
            <BalanceCell
              minutes={item.workday.balanceMinutes}
              compensatedMinutes={item.workday.deficitCompensatedMinutes}
            />
          ) : (
            '—'
          )}
        </td>
        <td className="px-5 py-4 text-center text-[13px] text-muted-foreground">
          {item && item.incidents.length > 0 ? (
            item.incidents.map((i) => INCIDENT_LABEL[i]).join(' · ')
          ) : (
            <span className="text-muted-foreground">—</span>
          )}
        </td>
      </tr>
      {clickable && isOpen ? (
        <tr className="border-b border-border bg-paper/60">
          <td colSpan={7} className="px-7 py-5">
            <DayDetail
              workdayId={item?.workday.id ?? null}
              targetDate={item?.workday.date.slice(0, 10) ?? dayKey}
              balanceMinutes={item?.workday.balanceMinutes ?? 0}
              deficitCompensatedMinutes={item?.workday.deficitCompensatedMinutes ?? 0}
            />
          </td>
        </tr>
      ) : null}
    </>
  );
}

function StatusPill({ status }: { status: WorkdayStatus }) {
  const m: Record<WorkdayStatus, { label: string; tone: 'green' | 'neutral' | 'amber' }> = {
    OPEN: { label: 'Abierta', tone: 'green' },
    CLOSED: { label: 'Cerrada', tone: 'neutral' },
    INCIDENT: { label: 'Incidencia', tone: 'amber' },
  };
  const { label, tone } = m[status];
  return <Badge tone={tone}>{label}</Badge>;
}

function CausePill({ cause }: { cause: RangeCause }) {
  // Punto con el color del tipo (vacaciones/baja/permiso/viaje); festivo
  // en gris. El label diferencia "Viaje de trabajo" de una ausencia real.
  const dot = cause.color ?? '#94a3b8';
  const text = cause.kind === 'HOLIDAY' ? `Festivo: ${cause.label}` : cause.label;
  return (
    <span className="inline-flex items-center gap-1.5 rounded-sm border border-border bg-paper px-2 py-0.5 text-[12px] font-medium text-foreground">
      <span className="h-2 w-2 rounded-full" style={{ backgroundColor: dot }} aria-hidden />
      {text}
    </span>
  );
}

function ExtraCell({
  minutes,
  status,
}: {
  minutes: number;
  status: OvertimeApprovalStatus | null;
}) {
  // Solo se muestra el exceso del día si el empleado lo aceptó (marcó
  // "a compensar") al fichar salida. Si hizo más horas que su jornada
  // pero no lo aprobó (pendiente o rechazado), aquí no aparece nada.
  if (minutes <= 0 || status !== 'APPROVED') {
    return <span className="text-muted-foreground">—</span>;
  }
  return <span className="text-success">+{formatDuration(minutes)}</span>;
}

function BalanceCell({
  minutes,
  compensatedMinutes,
}: {
  minutes: number;
  compensatedMinutes: number;
}) {
  // Día con déficit saldado (total o parcial) con la bolsa de horas.
  if (compensatedMinutes > 0) {
    return (
      <span className="inline-flex flex-col items-center gap-0.5">
        <span className="inline-flex items-center rounded-sm bg-success/10 px-1.5 py-0.5 text-[11px] font-medium text-success">
          Compensado
        </span>
        {minutes < 0 ? (
          <span className="text-destructive text-[11px]">−{formatDuration(Math.abs(minutes))}</span>
        ) : null}
      </span>
    );
  }
  if (minutes >= 0) return <span className="text-muted-foreground">—</span>;
  return <span className="text-destructive">−{formatDuration(Math.abs(minutes))}</span>;
}

function DayDetail({
  workdayId,
  targetDate,
  balanceMinutes,
  deficitCompensatedMinutes,
}: {
  /** null en días sin ningún fichaje: no existe fila Workday todavía. */
  workdayId: string | null;
  targetDate: string;
  balanceMinutes: number;
  deficitCompensatedMinutes: number;
}) {
  const queryClient = useQueryClient();
  const detail = useQuery({
    queryKey: ['workday-detail', workdayId],
    queryFn: () => api<DetailResponse>(`/workdays/${workdayId}`),
    enabled: workdayId !== null,
  });
  const [modalOpen, setModalOpen] = useState(false);
  // La compensación de déficit solo aplica a días pasados ya cerrados.
  const isPast = targetDate < ymd(new Date());

  if (workdayId !== null) {
    if (detail.isLoading) {
      return <p className="text-xs text-muted-foreground">Cargando detalle…</p>;
    }
    if (detail.error) {
      return (
        <p className="text-xs text-destructive">
          {detail.error instanceof ApiError ? detail.error.message : 'Error cargando detalle'}
        </p>
      );
    }
    if (!detail.data) return null;
  }
  // Sin Workday no hay fichajes que mostrar, pero sí se puede pedir corrección:
  // al aprobarla el backend crea la jornada (findOrCreateForDate).
  const punches = detail.data?.punches ?? [];

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <p
          className="font-mono text-[10px] font-medium uppercase text-muted-foreground"
          style={{ letterSpacing: '0.14em' }}
        >
          Fichajes
        </p>
        <Button type="button" size="sm" variant="ghost" onClick={() => setModalOpen(true)}>
          <Pencil className="h-3.5 w-3.5" />
          Solicitar corrección
        </Button>
      </div>

      {punches.length === 0 ? (
        <p className="text-xs text-muted-foreground">Sin fichajes registrados.</p>
      ) : (
        <ul className="border border-border rounded-sm bg-card overflow-hidden">
          {punches.map((p, idx) => (
            <li
              key={p.id}
              className={cn(
                'flex items-center justify-between px-3 py-2 text-sm',
                idx > 0 && 'border-t border-border',
              )}
            >
              <div className="flex items-center gap-2.5">
                <Badge tone={p.type === 'IN' ? 'green' : 'neutral'}>{p.type}</Badge>
                <span className="font-mono tabular-nums">{formatTime(p.eventAt)}</span>
              </div>
              <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                {p.declaredLocation === 'OFFICE' ? (
                  <Building2 className="h-3.5 w-3.5" />
                ) : (
                  <HomeIcon className="h-3.5 w-3.5" />
                )}
                <span>{p.declaredLocation === 'OFFICE' ? 'Oficina' : 'Remoto'}</span>
              </div>
            </li>
          ))}
        </ul>
      )}

      <VoidedPunchesSection items={detail.data?.voidedPunches ?? []} />

      {workdayId !== null && isPast && (balanceMinutes < 0 || deficitCompensatedMinutes > 0) ? (
        <DeficitCompensationSection
          workdayId={workdayId}
          balanceMinutes={balanceMinutes}
          deficitCompensatedMinutes={deficitCompensatedMinutes}
        />
      ) : null}

      <CorrectionRequestModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        targetDate={targetDate}
        punches={punches.map((p) => ({
          id: p.id,
          type: p.type,
          eventAt: p.eventAt,
          declaredLocation: p.declaredLocation,
        }))}
        onSubmitted={() => {
          // Un MANAGER se auto-aprueba sus propias correcciones, así que la
          // jornada puede haber cambiado ya al cerrar el modal.
          queryClient.invalidateQueries({ queryKey: ['workdays', 'range'] });
          queryClient.invalidateQueries({ queryKey: ['workday-detail'] });
        }}
      />
    </div>
  );
}

/**
 * Sección para saldar el déficit de un día con la bolsa de horas a compensar.
 * - Sin compensar: muestra el déficit, el saldo disponible y un botón para
 *   compensar (el mínimo entre déficit y saldo). Sin saldo → aviso.
 * - Ya compensado: muestra cuánto se saldó y permite deshacer.
 * Instantáneo, sin aprobación. El balance recibido es el NETO.
 */
function DeficitCompensationSection({
  workdayId,
  balanceMinutes,
  deficitCompensatedMinutes,
}: {
  workdayId: string;
  balanceMinutes: number;
  deficitCompensatedMinutes: number;
}) {
  const queryClient = useQueryClient();
  const balance = useQuery({
    queryKey: ['overtime', 'balance'],
    queryFn: () => api<OvertimeBalance>('/overtime/balance'),
  });

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ['overtime', 'balance'] });
    queryClient.invalidateQueries({ queryKey: ['workdays', 'range'] });
    queryClient.invalidateQueries({ queryKey: ['workday-detail', workdayId] });
  };

  const compensateMut = useMutation({
    mutationFn: () => api(`/overtime/workday/${workdayId}/compensate`, { method: 'POST' }),
    onSuccess: invalidate,
  });
  const revertMut = useMutation({
    mutationFn: () => api(`/overtime/workday/${workdayId}/compensate/revert`, { method: 'POST' }),
    onSuccess: invalidate,
  });

  const mutationError =
    compensateMut.error || revertMut.error
      ? (compensateMut.error ?? revertMut.error) instanceof ApiError
        ? ((compensateMut.error ?? revertMut.error) as ApiError).message
        : 'No se pudo completar la operación'
      : null;

  const isCompensated = deficitCompensatedMinutes > 0;
  // Déficit pendiente (no saldado). balanceMinutes es neto: <0 = aún hay deuda.
  const remainingDeficit = balanceMinutes < 0 ? -balanceMinutes : 0;
  const available = balance.data?.availableMinutes ?? 0;
  const canCompensate = Math.min(remainingDeficit, available);

  return (
    <div className="rounded-sm border border-border bg-card p-4 flex flex-col gap-2.5">
      <p
        className="font-mono text-[10px] font-medium uppercase text-muted-foreground"
        style={{ letterSpacing: '0.14em' }}
      >
        Horas a compensar
      </p>

      {isCompensated ? (
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <p className="text-[13px] text-foreground">
            <span className="inline-flex items-center gap-1.5 rounded-sm bg-success/10 px-2 py-0.5 text-success font-medium">
              <Scale className="h-3.5 w-3.5" />
              Compensado {formatDuration(deficitCompensatedMinutes)}
            </span>{' '}
            con tu saldo de horas a compensar.
            {remainingDeficit > 0 ? (
              <span className="text-muted-foreground">
                {' '}
                Queda un déficit de {formatDuration(remainingDeficit)} sin saldar.
              </span>
            ) : null}
          </p>
          <Button
            type="button"
            size="sm"
            variant="ghost"
            onClick={() => revertMut.mutate()}
            disabled={revertMut.isPending}
          >
            <Undo2 className="h-3.5 w-3.5" />
            {revertMut.isPending ? 'Deshaciendo…' : 'Deshacer'}
          </Button>
        </div>
      ) : (
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <p className="text-[13px] text-muted-foreground">
            Este día tiene un déficit de{' '}
            <span className="text-destructive font-medium">{formatDuration(remainingDeficit)}</span>
            .{' '}
            {balance.isLoading
              ? 'Cargando tu saldo…'
              : available > 0
                ? `Saldo disponible: ${formatDuration(available)}.`
                : 'No tienes saldo de horas a compensar disponible.'}
          </p>
          {available > 0 ? (
            <Button
              type="button"
              size="sm"
              variant="primary"
              onClick={() => compensateMut.mutate()}
              disabled={compensateMut.isPending || canCompensate <= 0}
            >
              <Scale className="h-3.5 w-3.5" />
              {compensateMut.isPending
                ? 'Compensando…'
                : `Compensar ${formatDuration(canCompensate)}`}
            </Button>
          ) : null}
        </div>
      )}

      {mutationError ? (
        <p role="alert" className="text-[12px] text-destructive">
          {mutationError}
        </p>
      ) : null}
    </div>
  );
}

function ymd(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function sameYmd(a: Date, b: Date): boolean {
  return ymd(a) === ymd(b);
}

function stripTime(d: Date): Date {
  const r = new Date(d);
  r.setHours(0, 0, 0, 0);
  return r;
}
